import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import axios from "axios";
import apiPaths from "app/utils/apiPaths";
import {
  clearTenantContext,
  extractTenantSlugFromHostname,
  isGenericAppHost,
  persistTenantContext,
  readTenantContext,
} from "app/utils/tenantHost";
import { absoluteApiUrl, resolveLogoSrc } from "./tenantBranding";

const TenantContext = createContext({
  ready: false,
  isTenantHost: false,
  tenantSlug: null,
  appsCode: null,
  hospitalName: null,
  tenantUrl: null,
  logoUrl: null,
  faviconUrl: null,
  hasLogo: false,
  hasFavicon: false,
  theme: null,
  address: null,
  error: null,
});

function resolveApiBase() {
  const raw = window._env_?.REACT_APP_API_ENDPOINT || "http://localhost:9090/";
  return String(raw).endsWith("/") ? String(raw) : `${raw}/`;
}

function TenantNotFound({ slug, message }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)",
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 480, textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#0F172A" }}>
          Hospital not found
        </Typography>
        <Typography sx={{ color: "#64748B", lineHeight: 1.6 }}>
          {message ||
            (slug
              ? `No active hospital workspace is available at “${slug}”.`
              : "This hospital URL is not available.")}
        </Typography>
        <Button
          variant="contained"
          href="https://app.cheritto.com/session/signin"
          sx={{ textTransform: "none", alignSelf: "center" }}
        >
          Go to Cheritto App
        </Button>
      </Stack>
    </Box>
  );
}

/**
 * Resolves tenant from hostname before the rest of the app runs.
 * Generic hosts (app.cheritto.com / localhost) skip resolution.
 */
export function TenantHostProvider({ children }) {
  const slug = useMemo(() => extractTenantSlugFromHostname(), []);
  const generic = useMemo(() => isGenericAppHost(), []);
  const [state, setState] = useState(() => ({
    ready: generic,
    isTenantHost: !generic && !!slug,
    tenantSlug: slug,
    appsCode: null,
    hospitalName: null,
    tenantUrl: null,
    logoUrl: null,
    faviconUrl: null,
    hasLogo: false,
    hasFavicon: false,
    theme: null,
    address: null,
    error: null,
  }));

  useEffect(() => {
    if (generic || !slug) {
      clearTenantContext();
      setState({
        ready: true,
        isTenantHost: false,
        tenantSlug: null,
        appsCode: null,
        hospitalName: null,
        tenantUrl: null,
        logoUrl: null,
        faviconUrl: null,
        hasLogo: false,
        hasFavicon: false,
        theme: null,
        address: null,
        error: null,
      });
      return;
    }

    let cancelled = false;
    const cached = readTenantContext();
    if (cached?.tenantSlug === slug && cached?.appsCode) {
      localStorage.setItem("appsCode", cached.appsCode);
    }

    (async () => {
      try {
        const res = await axios.get(`${resolveApiBase()}${apiPaths.TENANT_RESOLVE}`, {
          params: { slug, host: window.location.hostname },
        });
        if (cancelled) return;
        const data = res.data || {};
        if (!data.appsCode) {
          throw new Error("TENANT_NOT_FOUND");
        }
        const logoUrl = absoluteApiUrl(data.logoUrl);
        const faviconUrl = absoluteApiUrl(data.faviconUrl);
        const next = {
          ...data,
          logoUrl,
          faviconUrl,
          hasLogo: Boolean(data.hasLogo && logoUrl),
          hasFavicon: Boolean(data.hasFavicon && faviconUrl),
          theme: data.theme || null,
        };
        persistTenantContext(next);
        localStorage.setItem("appsCode", String(data.appsCode).trim());
        setState({
          ready: true,
          isTenantHost: true,
          tenantSlug: data.tenantSlug || slug,
          appsCode: data.appsCode,
          hospitalName: data.hospitalName || null,
          tenantUrl: data.tenantUrl || null,
          logoUrl,
          faviconUrl,
          hasLogo: next.hasLogo,
          hasFavicon: next.hasFavicon,
          theme: next.theme,
          address: data.address || null,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        clearTenantContext();
        const status = err?.response?.status;
        const apiError = err?.response?.data?.error;
        const message =
          err?.response?.data?.message ||
          (status === 403
            ? "This hospital workspace is not available."
            : "No hospital found for this URL.");
        setState({
          ready: true,
          isTenantHost: true,
          tenantSlug: slug,
          appsCode: null,
          hospitalName: null,
          tenantUrl: null,
          logoUrl: null,
          faviconUrl: null,
          hasLogo: false,
          hasFavicon: false,
          theme: null,
          address: null,
          error: apiError || message,
          errorMessage: message,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [generic, slug]);

  if (!state.ready) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <Stack spacing={1.5} alignItems="center">
          <CircularProgress size={28} />
          <Typography sx={{ color: "#64748B" }}>Loading hospital workspace…</Typography>
        </Stack>
      </Box>
    );
  }

  if (state.isTenantHost && !state.appsCode) {
    return <TenantNotFound slug={state.tenantSlug} message={state.errorMessage || state.error} />;
  }

  return <TenantContext.Provider value={state}>{children}</TenantContext.Provider>;
}

export function useTenantHost() {
  return useContext(TenantContext);
}

/** Default global logo path for app.cheritto.com auth screens. */
export function useAuthBrandLogoSrc() {
  const tenantHost = useTenantHost();
  if (tenantHost?.isTenantHost && tenantHost.logoUrl) {
    return tenantHost.logoUrl;
  }
  return resolveLogoSrc({});
}

export default TenantHostProvider;
